import { getApiUrl } from '../config/api.config.jsx';

const LEADERSHIP_HEADER = 'X-Leadership-Api-Key';

function leadershipApiKey() {
  const key = process.env.REACT_APP_LEADERSHIP_API_KEY;
  return key && String(key).trim() ? String(key).trim() : '';
}

/**
 * GET /loan-applications/leadership-report — partner leadership pipeline view.
 * Requires REACT_APP_LEADERSHIP_API_KEY for X-Leadership-Api-Key (see backend).
 */
export async function fetchLeadershipReport({ pageNumber = 1, pageSize = 100 } = {}) {
  const apiKey = leadershipApiKey();
  if (!apiKey) {
    throw new Error(
      'Leadership API key is not configured. Set REACT_APP_LEADERSHIP_API_KEY in your environment.'
    );
  }

  const params = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });

  const url = `${getApiUrl()}/loan-applications/leadership-report?${params.toString()}`;
  const response = await fetch(url, {
    headers: {
      [LEADERSHIP_HEADER]: apiKey,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Leadership report failed (${response.status})`);
  }

  return response.json();
}

/**
 * Normalize varying backend shapes into a flat row for UI + metrics.
 */
export function normalizeLeadershipRow(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const nestedBusiness =
    raw.businessInfo && typeof raw.businessInfo === 'object' ? raw.businessInfo : {};
  const nestedContact =
    raw.contactInfo && typeof raw.contactInfo === 'object' ? raw.contactInfo : {};
  const nestedAddress =
    raw.businessAddress && typeof raw.businessAddress === 'object' ? raw.businessAddress : {};

  const pick = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
  };

  const amountRaw = pick(
    raw.loanAmount,
    raw.requestedAmount,
    raw.amount,
    raw.desiredLoanAmount
  );
  let loanAmountNum = null;
  if (amountRaw !== '' && amountRaw != null) {
    const n = Number(String(amountRaw).replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(n)) loanAmountNum = n;
  }

  const id = pick(raw.id, raw.applicationId, raw.loanApplicationId, raw.uuid) || '';

  const first = pick(nestedContact.firstName, raw.firstName);
  const last = pick(nestedContact.lastName, raw.lastName);
  const contactName = [first, last].filter(Boolean).join(' ').trim();

  return {
    id: String(id || Math.random().toString(36).slice(2)),
    status: String(pick(raw.status, raw.applicationStatus, raw.stage, raw.pipelineStage) || '')
      .toLowerCase()
      .replace(/\s+/g, '_'),
    statusLabel: pick(raw.status, raw.applicationStatus, raw.stage, raw.pipelineStage) || '—',
    loanAmountLabel: amountRaw !== '' && amountRaw != null ? String(amountRaw) : '—',
    loanAmountNum,
    loanPurpose: pick(raw.loanPurpose, raw.purpose, raw.financingPurpose) || '—',
    businessName: pick(
      nestedBusiness.businessName,
      raw.businessName,
      raw.companyName
    ) || '—',
    businessType: pick(nestedBusiness.businessType, raw.businessType) || '—',
    businessIndustry: pick(nestedBusiness.businessIndustry, raw.businessIndustry, raw.industry) || '—',
    city: pick(nestedAddress.city, raw.city) || '',
    state: pick(nestedAddress.state, raw.state) || '',
    email: pick(nestedContact.email, raw.email, raw.contactEmail) || '—',
    contactName: contactName || '—',
    submittedAt: pick(raw.createdAt, raw.submittedAt, raw.appliedAt, raw.created_at),
    raw,
  };
}

export function parseLeadershipReportResponse(json) {
  const root = json?.data !== undefined ? json.data : json;
  let rows = [];

  if (Array.isArray(root)) {
    rows = root;
  } else if (root && typeof root === 'object') {
    rows =
      root.items ||
      root.applications ||
      root.records ||
      root.loanApplications ||
      root.results ||
      [];
    if (!Array.isArray(rows)) rows = [];
  }

  const total =
    (root && typeof root === 'object' && !Array.isArray(root)
      ? root.totalCount ?? root.total ?? root.count ?? root.totalRecords
      : null) ?? rows.length;

  const pageNumber =
    (root && typeof root === 'object' && !Array.isArray(root)
      ? root.pageNumber ?? root.page
      : null) ?? 1;

  const pageSize =
    (root && typeof root === 'object' && !Array.isArray(root)
      ? root.pageSize ?? root.limit
      : null) ?? rows.length;

  const normalized = rows.map(normalizeLeadershipRow).filter(Boolean);

  return {
    rows: normalized,
    total: typeof total === 'number' ? total : Number(total) || normalized.length,
    pageNumber: typeof pageNumber === 'number' ? pageNumber : Number(pageNumber) || 1,
    pageSize: typeof pageSize === 'number' ? pageSize : Number(pageSize) || normalized.length,
  };
}
